/**
 * Signup page — /signup
 *
 * PRE-72: Public SMS-consent signup surface required to unblock Twilio A2P 10DLC
 * campaign QE2c6890da8086d771620e9b13fadeba0b and Toll-Free verification
 * HH1ef6dea39aa7428b1cb794ad0488ceb7 (both were rejected with codes 30909/30511
 * because reviewers could not find a public SMS opt-in surface on prest0.ai).
 *
 * Unlike /start (trial-purchase flow that assumes an access code from Sales),
 * /signup is a purely public anonymous flow: anyone can create a Prest0n
 * account here, opt in to SMS, and trigger a Twilio double-opt-in confirmation.
 *
 * Flow summary:
 *   1. User fills name, email, phone (US), optional firm, password, and two
 *      independent unchecked checkboxes (SMS consent + ToS/Privacy).
 *   2. On submit we call Firebase Auth `createUserWithEmailAndPassword` directly
 *      to mint the new user. This page does NOT attempt any code-redemption or
 *      trial flow; that belongs to /start.
 *   3. We write a consent audit doc at `prest0n_sms_consent/{uid}` with the
 *      client-observable fields (phone E.164, UA, timestamps, etc.).
 *   4. We call the `sendDoubleOptInSMS` callable — the Cloud Function stamps the
 *      trusted IP header onto the consent doc and fires the Twilio double-opt-in
 *      SMS ("Reply YES to confirm").
 *   5. We show a "Check your phone" screen with a Resend button.
 *
 * Accessibility: real <label> bindings, aria-live error region, tab order
 * follows visual order, submit button disabled until the form is fully valid.
 *
 * This page is public — it MUST NOT be wrapped in ProtectedRoute.
 */

import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { requestBrandedVerificationEmail } from '../lib/identity/signInOrLink';
import { recordSignupWhileSignedIn } from '../utils/signOutBreadcrumb';
import { getFirestore, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import SeoHead from '../components/SeoHead';
import PasswordInput from '../components/common/PasswordInput';

// ============================================================================
// Helpers — validation + formatting
// ============================================================================

/**
 * Normalize a user-supplied US phone number to E.164.
 * Accepts common formats: "(628) 444-4018", "628-444-4018", "16284444018",
 * "+1 628 444 4018". Returns null if the input is not a valid 10-digit US
 * number (ignoring an optional leading 1).
 *
 * Exported for unit tests (see tests/signup/Signup.test.tsx).
 */
export function normalizePhoneE164(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  const digits = raw.replace(/\D+/g, '');
  // 10 digits: US number without country code
  if (digits.length === 10) return `+1${digits}`;
  // 11 digits starting with 1: US number with country code
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

/**
 * Validate password per PRE-72 spec: min 10 chars, mixed case (at least one
 * lowercase AND one uppercase), at least one digit.
 */
export function validatePassword(pw: string): { ok: boolean; reason?: string } {
  if (!pw || pw.length < 10) return { ok: false, reason: 'Password must be at least 10 characters.' };
  if (!/[a-z]/.test(pw)) return { ok: false, reason: 'Password must include a lowercase letter.' };
  if (!/[A-Z]/.test(pw)) return { ok: false, reason: 'Password must include an uppercase letter.' };
  if (!/\d/.test(pw)) return { ok: false, reason: 'Password must include a number.' };
  return { ok: true };
}

/**
 * Pure predicate that decides whether the /signup submit button should be
 * enabled. Used both by the React `useMemo` below and by unit tests that
 * exercise the checkbox-gate requirement (PRE-72).
 *
 * Submit must ONLY be enabled when:
 *   - both checkboxes (SMS consent + ToS) are ticked,
 *   - all required text fields are non-empty,
 *   - the phone parses to a valid E.164 US number,
 *   - the password meets the strength requirements.
 */
export function isSignupSubmittable(input: {
  name: string;
  email: string;
  phone: string;
  password: string;
  smsConsent: boolean;
  tosAccepted: boolean;
}): boolean {
  // SMS consent is OPTIONAL per Twilio A2P rule 30923 (consent cannot be a
  // required condition of service). Only ToS is required to submit.
  if (!input.tosAccepted) return false;
  if (!input.name.trim()) return false;
  if (!input.email.trim()) return false;
  if (!input.phone.trim()) return false;
  if (normalizePhoneE164(input.phone) == null) return false;
  if (!validatePassword(input.password).ok) return false;
  return true;
}

// ============================================================================
// Styling (matches existing Prest0 public-page aesthetic — StartTrial / Privacy)
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.main`
  background: var(--brand-canvas);
  min-height: 100vh;
  padding: 0 0 5rem;
`;

const Hero = styled.section`
  max-width: 560px;
  margin: 0 auto;
  padding: 5rem 1.5rem 1.5rem;
  text-align: center;
  animation: ${fadeIn} 0.45s ease both;
`;

const HeroTitle = styled.h1`
  font-family: 'Space Grotesk', 'Inter', -apple-system, sans-serif;
  font-size: 2.1rem;
  font-weight: 500;
  letter-spacing: -0.03em;
  color: var(--brand-text);
  line-height: 1.2;
  margin-bottom: 0.6rem;
`;

const HeroSub = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

const FormCard = styled.form`
  max-width: 520px;
  margin: 1.5rem auto 0;
  padding: 1.5rem;
  background: #fff;
  border: 1px solid #E6E4DE;
  border-radius: 1rem;
  animation: ${fadeIn} 0.5s 0.08s ease both;

  @media (min-width: 640px) {
    padding: 2rem;
  }
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
`;

const FieldLabel = styled.label`
  font-size: 0.82rem;
  font-weight: 600;
  color: #333;
  letter-spacing: -0.005em;
`;

const FieldHint = styled.span`
  font-size: 0.72rem;
  color: var(--brand-text-muted);
  font-weight: 400;
`;

const FieldInput = styled.input`
  border: 1px solid #E0DED8;
  border-radius: 0.6rem;
  padding: 0.75rem 0.9rem;
  font-size: 0.95rem;
  font-family: inherit;
  background: #fff;
  color: var(--brand-text);
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: var(--brand-text);
    box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.08);
  }

  &:disabled { background: #F5F4F0; cursor: not-allowed; }
`;

const CheckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.6rem 0;
  cursor: pointer;
  font-size: 0.82rem;
  color: #444;
  line-height: 1.55;

  input {
    margin-top: 0.18rem;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    accent-color: var(--brand-text);
    cursor: pointer;
  }

  a { color: var(--brand-text); text-decoration: underline; }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: var(--brand-text);
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  padding: 0.95rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  margin-top: 0.75rem;
  transition: background 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) { background: #2a2a2a; }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    background: #333;
  }
`;

const ErrorBox = styled.div`
  background: #FEF3F2;
  border: 1px solid #FDA29B;
  color: #B42318;
  padding: 0.6rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.82rem;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const FooterNote = styled.div`
  max-width: 560px;
  margin: 2.5rem auto 0;
  padding: 0 1.25rem;
  text-align: center;
  font-size: 0.8rem;
  color: var(--brand-text-muted);
  line-height: 1.7;

  a { color: #555; text-decoration: underline; &:hover { color: var(--brand-text); } }
`;

const SuccessWrap = styled.section`
  max-width: 520px;
  margin: 2.5rem auto 0;
  padding: 2rem;
  background: #fff;
  border: 1px solid #E6E4DE;
  border-radius: 1rem;
  text-align: center;
  animation: ${fadeIn} 0.4s ease both;
`;

const SuccessIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
`;

const SuccessTitle = styled.h2`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.4rem;
  font-weight: 500;
  color: var(--brand-text);
  margin-bottom: 0.5rem;
`;

const SuccessText = styled.p`
  color: #555;
  font-size: 0.92rem;
  line-height: 1.6;
  margin-bottom: 1.25rem;
`;

const ResendButton = styled.button`
  background: transparent;
  border: 1px solid #D0CEC8;
  border-radius: 0.6rem;
  padding: 0.6rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--brand-text);
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) { background: #F5F4F0; border-color: var(--brand-text); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ============================================================================
// Component
// ============================================================================

type FormState = {
  name: string;
  email: string;
  phone: string;
  firmName: string;
  password: string;
  smsConsent: boolean;
  tosAccepted: boolean;
};

const INITIAL_FORM: FormState = {
  name: '',
  email: '',
  phone: '',
  firmName: '',
  password: '',
  smsConsent: false,
  tosAccepted: false,
};

const Signup: React.FC = () => {
  const navigate = useNavigate();
  // PR-F0 A3 (2026-09-21): the 01:08Z sign-out on /desktop was preceded by a
  // `/signup` navigation while signed in that nothing explained. Record the
  // mount for an already signed-in (non-anonymous) person — diagnostics only.
  useEffect(() => {
    try {
      const current = getAuth().currentUser;
      if (current && !current.isAnonymous) recordSignupWhileSignedIn();
    } catch { /* diagnostics never throw */ }
  }, []);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneUid, setDoneUid] = useState<string | null>(null);
  // Whether the user opted into SMS at submit time. Determines which success
  // screen to render (full double-opt-in flow vs. simple "account created").
  const [doneSmsOptedIn, setDoneSmsOptedIn] = useState<boolean>(false);
  // Where "continue to setup" goes from the check-your-phone screen — kept
  // on screen (not auto-navigated) so the Resend affordance stays reachable.
  const [doneTarget, setDoneTarget] = useState<string>('/onboarding');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  // Derived: is submit enabled? Both checkboxes + all required fields + valid
  // phone + valid password. Firm is optional. Delegates to the pure helper
  // so tests can assert identical behaviour without rendering the component.
  const formValid = useMemo(() => isSignupSubmittable(form), [form]);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !formValid) return;

    setError(null);
    setSubmitting(true);

    const phoneE164 = normalizePhoneE164(form.phone);
    if (!phoneE164) {
      setError('Please enter a valid US phone number.');
      setSubmitting(false);
      return;
    }

    const pwCheck = validatePassword(form.password);
    if (!pwCheck.ok) {
      setError(pwCheck.reason || 'Password does not meet requirements.');
      setSubmitting(false);
      return;
    }

    try {
      const auth = getAuth();
      // 1) Create the Firebase Auth user.
      const cred = await createUserWithEmailAndPassword(auth, form.email.trim().toLowerCase(), form.password);
      const uid = cred.user.uid;

      // The typed full name becomes the account's displayName (2026-08-25):
      // this page was the ONLY surface capturing a real name, and it used to
      // strand it in the consent doc — downstream identity (onboarding
      // prefill, "[first name]'s Workspace") reads auth displayName.
      try {
        const { updateProfile } = await import('firebase/auth');
        const typedName = form.name.trim().replace(/\s+/g, ' ');
        if (typedName) await updateProfile(cred.user, { displayName: typedName });
      } catch { /* non-fatal — onboarding re-captures the name */ }

      // Best-effort email verification (non-fatal).
      // Branded verification email (task #50) — fire-and-forget; the shared
      // helper falls back to Firebase's default template if the callable is
      // unreachable (2026-08-14: consolidated from an inline duplicate).
      void requestBrandedVerificationEmail(cred.user);

      // 2) Write consent audit doc. smsConsentIpAddress is intentionally left
      //    for the callable to stamp server-side (trusted header).
      const db = getFirestore();
      await setDoc(doc(db, 'prest0n_sms_consent', uid), {
        uid,
        email: form.email.trim().toLowerCase(),
        phone: phoneE164,
        name: form.name.trim(),
        firmName: form.firmName.trim() || null,
        smsConsent: form.smsConsent === true,
        smsConsentTimestamp: serverTimestamp(),
        smsConsentUserAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        tosAccepted: true,
        tosAcceptedTimestamp: serverTimestamp(),
        doubleOptInStatus: form.smsConsent === true ? 'pending' : 'not_opted_in',
        source: 'prest0.ai/signup',
      });

      // 3) Fire the double-opt-in callable ONLY when the user opted in.
      //    SMS consent is optional (Twilio A2P rule 30923) — do not send a
      //    confirmation text to someone who did not check the SMS box.
      if (form.smsConsent === true) {
        try {
          const fns = getFunctions(undefined, 'us-central1');
          const callable = httpsCallable<{ uid: string }, { ok: boolean }>(fns, 'sendDoubleOptInSMS');
          await callable({ uid });
        } catch (callErr: any) {
          // Do not block the UX — the user still has a pending consent doc we can
          // retry via the Resend button.
          // eslint-disable-next-line no-console
          console.warn('[signup] sendDoubleOptInSMS failed (non-fatal):', callErr?.message || callErr);
        }
      }

      // 4) Organic provisioning + into onboarding (task #74: EVERY
      //    account-creation path gets the same staged onboarding — this page
      //    used to dead-end on a static "account created" screen). Review
      //    round 2026-08-18: code/team-invite holders are EXCLUDED — same
      //    contract as AuthFormSection — and refusal reasons route honestly.
      let pendingCode = false;
      try { pendingCode = Boolean(sessionStorage.getItem('pending_trial_code')); } catch { /* default organic */ }
      let target = '/onboarding';
      if (pendingCode) {
        // A held trial code belongs to the /start redemption journey — no
        // founder-org provisioning here.
        target = '/start';
      } else {
        try {
          const fns = getFunctions(undefined, 'us-central1');
          const startOrganicTrial = httpsCallable(fns, 'startOrganicTrial');
          const result: any = await startOrganicTrial({});
          const r = (result as any)?.data || {};
          if (r?.started === false && r?.reason === 'pending-team-invite') {
            // A team invitation is waiting — '/' lets PendingInviteGate
            // funnel them into their team instead of founder onboarding.
            target = '/';
          } else if (r?.started === false && r?.reason === 'is-workspace-member') {
            target = '/';
          }
        } catch (e) {
          console.warn('[signup] startOrganicTrial failed (non-fatal):', e);
        }
      }
      // Prefill handoff: the company + phone typed here must not be retyped
      // in onboarding (review round 2026-08-18).
      try {
        sessionStorage.setItem('prest0_signup_prefill', JSON.stringify({
          company: form.firmName || '', phone: form.phone || '',
        }));
      } catch { /* prefill is best-effort */ }
      setDoneSmsOptedIn(form.smsConsent === true);
      setDoneUid(uid);
      if (form.smsConsent === true) {
        // Keep the "check your phone" screen reachable — it carries the
        // Resend button for a failed double-opt-in SMS; its primary CTA
        // continues into the journey.
        setDoneTarget(target);
      } else {
        navigate(target);
      }
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in instead.');
      } else if (code === 'auth/weak-password') {
        setError('Password is too weak. Use at least 10 characters with mixed case and a number.');
      } else if (code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Something went wrong while creating your account. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!doneUid || resending) return;
    setResending(true);
    setResendMessage(null);
    try {
      const fns = getFunctions(undefined, 'us-central1');
      const callable = httpsCallable<{ uid: string }, { ok: boolean }>(fns, 'sendDoubleOptInSMS');
      await callable({ uid: doneUid });
      setResendMessage('Sent — check your phone again in a moment.');
    } catch {
      setResendMessage('Could not resend right now. Try again in a minute.');
    } finally {
      setResending(false);
    }
  };

  // --- Success views ----------------------------------------------------------
  // Two variants depending on whether the user opted into SMS:
  //   - SMS opt-in → "Check your phone" double-opt-in flow.
  //   - No SMS    → "Account created" simple confirmation (no Twilio send).
  if (doneUid && !doneSmsOptedIn) {
    return (
      <Page>
        <SeoHead
          title="Account created — prest0"
          description="Your Prest0n account has been created. Check your email for a verification link."
          canonicalUrl="https://prest0.ai/signup"
        />
        <Hero>
          <HeroTitle>Account created</HeroTitle>
          <HeroSub>You're all set. Check your email for the verification link.</HeroSub>
        </Hero>
        <SuccessWrap>
          <SuccessIcon aria-hidden="true">✅</SuccessIcon>
          <SuccessTitle>You're in</SuccessTitle>
          <SuccessText>
            You did not opt in to SMS notifications. That's fine — you can opt
            in later from your account settings if you change your mind.
          </SuccessText>
          <div style={{ marginTop: '1.25rem', fontSize: '0.85rem', color: '#555' }}>
            <button
              type="button"
              onClick={() => navigate('/auth', { state: { returnUrl: '/signup' } })}
              style={{ background: 'none', border: 'none', color: 'var(--brand-text)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' }}
            >
              Sign in to your new account
            </button>
          </div>
        </SuccessWrap>
      </Page>
    );
  }

  if (doneUid) {
    return (
      <Page>
        <SeoHead
          title="Check your phone — prest0"
          description="Confirm your SMS opt-in by replying YES to the text message we just sent."
          canonicalUrl="https://prest0.ai/signup"
        />
        <Hero>
          <HeroTitle>Check your phone</HeroTitle>
          <HeroSub>We just sent a confirmation text. Reply <strong>YES</strong> to finish opting in.</HeroSub>
        </Hero>
        <SuccessWrap>
          <SuccessIcon aria-hidden="true">📱</SuccessIcon>
          <SuccessTitle>One more step</SuccessTitle>
          <SuccessText>
            Reply <strong>YES</strong> to the text we just sent. You can reply <strong>STOP</strong>
            {' '}at any time to opt out, or <strong>HELP</strong> for support.
          </SuccessText>
          <ResendButton
            type="button"
            onClick={() => navigate(doneTarget)}
            style={{ background: 'var(--brand-text)', color: '#fff', borderColor: 'var(--brand-text)', marginRight: '0.6rem' }}
          >
            continue to setup →
          </ResendButton>
          <ResendButton type="button" onClick={handleResend} disabled={resending}>
            {resending ? 'Resending…' : 'Resend confirmation SMS'}
          </ResendButton>
          {resendMessage && (
            <div role="status" aria-live="polite" style={{ marginTop: '0.75rem', fontSize: '0.82rem', color: '#555' }}>
              {resendMessage}
            </div>
          )}
          <div style={{ marginTop: '1.25rem', fontSize: '0.8rem', color: '#888' }}>
            Didn't get the text?{' '}
            <button
              type="button"
              onClick={() => navigate('/auth', { state: { returnUrl: '/signup' } })}
              style={{ background: 'none', border: 'none', color: '#555', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.8rem' }}
            >
              Sign in to your new account
            </button>
            {' '}and you can retry from account settings.
          </div>
        </SuccessWrap>
      </Page>
    );
  }

  // --- Signup form -----------------------------------------------------------
  return (
    <Page>
      <SeoHead
        title="Create your Prest0n account — prest0"
        description="Create a Prest0n account and opt in to SMS notifications for account and client-communication events."
        canonicalUrl="https://prest0.ai/signup"
      />
      <Hero>
        <HeroTitle>Create your Prest0n account</HeroTitle>
        <HeroSub>
          Sign up for free. SMS notifications for your account and client communications are optional.
        </HeroSub>
      </Hero>

      <FormCard onSubmit={handleSubmit} noValidate aria-label="Create account">
        {/* aria-live region so screen readers announce server-side and validation errors */}
        <div role="alert" aria-live="assertive">
          {error && <ErrorBox>{error}</ErrorBox>}
        </div>

        <FieldRow>
          <FieldLabel htmlFor="signup-name">
            Full name <span style={{ color: '#B42318' }} aria-hidden="true">*</span>
          </FieldLabel>
          <FieldInput
            id="signup-name"
            name="name"
            type="text"
            autoComplete="name"
            required
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            disabled={submitting}
          />
        </FieldRow>

        <FieldRow>
          <FieldLabel htmlFor="signup-email">
            Email <span style={{ color: '#B42318' }} aria-hidden="true">*</span>
          </FieldLabel>
          <FieldInput
            id="signup-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(e) => updateField('email', e.target.value)}
            disabled={submitting}
          />
        </FieldRow>

        <FieldRow>
          <FieldLabel htmlFor="signup-phone">
            Phone (US) <span style={{ color: '#B42318' }} aria-hidden="true">*</span>
            <FieldHint> — e.g. (628) 444-4018</FieldHint>
          </FieldLabel>
          <FieldInput
            id="signup-phone"
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            required
            value={form.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            disabled={submitting}
          />
        </FieldRow>

        <FieldRow>
          <FieldLabel htmlFor="signup-firm">
            Company <FieldHint>(optional)</FieldHint>
          </FieldLabel>
          <FieldInput
            id="signup-firm"
            name="firmName"
            type="text"
            autoComplete="organization"
            value={form.firmName}
            onChange={(e) => updateField('firmName', e.target.value)}
            disabled={submitting}
          />
        </FieldRow>

        <FieldRow>
          <FieldLabel htmlFor="signup-password">
            Password <span style={{ color: '#B42318' }} aria-hidden="true">*</span>
            <FieldHint> — at least 10 characters, mixed case, with a number</FieldHint>
          </FieldLabel>
          <PasswordInput
            inputComponent={FieldInput}
            id="signup-password"
            name="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={form.password}
            onChange={(e) => updateField('password', e.target.value)}
            disabled={submitting}
          />
        </FieldRow>

        {/* SMS consent — UNCHECKED BY DEFAULT, and OPTIONAL.
            Per Twilio A2P rule 30923, SMS consent cannot be a required
            condition of service: users must be able to create an account
            without ticking this box. The submit button is gated only on the
            ToS checkbox below. */}
        <CheckRow htmlFor="signup-sms-consent">
          <input
            id="signup-sms-consent"
            name="smsConsent"
            type="checkbox"
            checked={form.smsConsent}
            onChange={(e) => updateField('smsConsent', e.target.checked)}
            disabled={submitting}
          />
          <span>
            <strong>(Optional)</strong> I agree to receive SMS from Prest0n about my account
            and client-communication notifications. Msg &amp; data rates may apply. Reply STOP
            to opt out, HELP for help. Frequency varies. See{' '}
            <a href="/privacy-policy#sms">Privacy Policy</a> and{' '}
            <a href="/sms-terms">SMS Terms</a>.
          </span>
        </CheckRow>

        {/* Separate ToS/Privacy checkbox — also unchecked by default. */}
        <CheckRow htmlFor="signup-tos">
          <input
            id="signup-tos"
            name="tosAccepted"
            type="checkbox"
            checked={form.tosAccepted}
            onChange={(e) => updateField('tosAccepted', e.target.checked)}
            disabled={submitting}
          />
          <span>
            I agree to the <a href="/terms-of-service">Terms of Service</a> and{' '}
            <a href="/privacy-policy">Privacy Policy</a>.
          </span>
        </CheckRow>

        <SubmitButton type="submit" disabled={!formValid || submitting} aria-disabled={!formValid || submitting}>
          {submitting ? 'Creating account…' : 'Create account'}
        </SubmitButton>
      </FormCard>

      <FooterNote>
        Already have an account?{' '}
        <Link to="/auth">Sign in</Link>
        {' '}·{' '}
        Need a trial access code? <Link to="/start">Start trial</Link>
      </FooterNote>
    </Page>
  );
};

export default Signup;
